/**
 * Configuration du tenant — la cadence de relance.
 *
 * Lue partout où le ton d'une relance se décide, pour que l'écran de facture
 * et la fonction planifiée ne puissent plus répondre différemment à la même
 * question. Les seuils vivaient en dur dans les deux, et avaient déjà
 * commencé à diverger.
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_BRANDING,
  DEFAULT_MARGIN_TARGET,
  DEFAULT_REMINDER_CADENCE,
  DomainError,
} from "@dker/core";
import type {
  MarginTarget,
  ReminderCadence,
  SaveOvertimeRatesInput,
  TenantBranding,
  TenantConfig,
} from "@dker/core";
import { applyBrandColor, PAGE_BACKGROUND } from "@/lib/branding";
import { serviceDeps, tenantConfigServices, useExecutionContext } from "@/services";
import { useTenantOptional } from "@/hooks/useTenant";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";

export function useTenantConfig() {
  const makeContext = useExecutionContext();
  const tenant = useTenantOptional();

  return useQuery<TenantConfig>({
    queryKey: ["tenant-config", tenant?.id],
    enabled: Boolean(tenant),
    queryFn: () => tenantConfigServices.lireConfiguration(makeContext(), {}, serviceDeps),
    // La cadence change une fois par an au mieux : la relire à chaque montage
    // d'écran ferait une requête par facture ouverte, pour une valeur qui ne
    // bouge pas.
    staleTime: 5 * 60_000,
  });
}

/**
 * La cadence en vigueur, utilisable sans attendre.
 *
 * Renvoie les valeurs par défaut tant que la lecture n'a pas abouti. C'est
 * volontaire : un écran qui n'affiche aucun ton de relance le temps d'un
 * aller-retour réseau serait plus déroutant qu'un écran qui affiche le ton
 * standard, lequel est de toute façon celui qu'appliquera le service si le
 * tenant n'a rien réglé.
 */
export function useReminderCadence(): ReminderCadence {
  const { data } = useTenantConfig();
  return data?.reminders ?? DEFAULT_REMINDER_CADENCE;
}

export function useSaveReminderCadence() {
  const makeContext = useExecutionContext();
  const queryClient = useQueryClient();
  const tenant = useTenantOptional();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (cadence: ReminderCadence) =>
      tenantConfigServices.enregistrerCadenceRelance(
        makeContext(),
        { cadence },
        serviceDeps,
      ),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-config", tenant?.id] });
      addToast({ type: "success", title: "Cadence de relance enregistrée" });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message:
          error instanceof DomainError
            ? error.message
            : "Une erreur inattendue est survenue.",
      }),
  });
}

/**
 * L'identité visuelle en vigueur, utilisable sans attendre.
 *
 * Comme pour la cadence : les valeurs par défaut le temps de l'aller-retour,
 * plutôt qu'un logo qui apparaît en sautant une frame.
 */
export function useBranding(): TenantBranding {
  const { data } = useTenantConfig();
  return data?.branding ?? DEFAULT_BRANDING;
}

/**
 * Pose la couleur du tenant sur les variables CSS, et la retire à la
 * déconnexion.
 *
 * Monté une seule fois, au-dessus de l'application. Le faire dans chaque
 * composant qui affiche un accent multiplierait les écritures sur le style du
 * document pour un résultat identique — et laisserait la couleur du client
 * précédent en place après un changement de session.
 */
export function useApplyBranding(): void {
  const branding = useBranding();
  // Le thème résolu, et non la préférence : l'encre d'accent se calcule
  // contre le fond de page réellement affiché, y compris quand « système »
  // bascule sans que personne n'ait touché au réglage.
  const resolvedTheme = useUiStore((s) => s.resolvedTheme);

  useEffect(() => {
    applyBrandColor(branding.primaryColor, PAGE_BACKGROUND[resolvedTheme]);
  }, [branding.primaryColor, resolvedTheme]);
}

export function useSaveBranding() {
  const makeContext = useExecutionContext();
  const queryClient = useQueryClient();
  const tenant = useTenantOptional();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (branding: TenantBranding) =>
      tenantConfigServices.enregistrerIdentiteVisuelle(
        makeContext(),
        { branding },
        serviceDeps,
      ),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-config", tenant?.id] });
      // Le nom et le logo de l'entreprise vivent encore dans le store
      // d'authentification, qui porte les colonnes de `tenants` : sans ce
      // rafraîchissement, la barre latérale garderait l'ancien logo jusqu'à
      // la prochaine connexion.
      await useAuthStore.getState().refreshTenant();
      addToast({ type: "success", title: "Identité visuelle enregistrée" });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message:
          error instanceof DomainError
            ? error.message
            : "Une erreur inattendue est survenue.",
      }),
  });
}

/**
 * L'objectif de marge en vigueur, utilisable sans attendre.
 *
 * `null` le temps de l'aller-retour, ce qui est exactement l'état « pas
 * d'objectif » : le tableau de bord affiche alors la comparaison au mois
 * précédent, puis bascule sur la jauge quand la valeur arrive. Une jauge qui
 * apparaît vaut mieux qu'une jauge qui saute d'une valeur inventée à la vraie.
 */
export function useMarginTarget(): MarginTarget {
  const { data } = useTenantConfig();
  return data?.marginTarget ?? DEFAULT_MARGIN_TARGET;
}

export function useSaveMarginTarget() {
  const makeContext = useExecutionContext();
  const queryClient = useQueryClient();
  const tenant = useTenantOptional();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (targetPercent: MarginTarget) =>
      tenantConfigServices.enregistrerObjectifMarge(
        makeContext(),
        { targetPercent },
        serviceDeps,
      ),

    onSuccess: async (config) => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-config", tenant?.id] });
      addToast({
        type: "success",
        title:
          config.marginTarget == null
            ? "Objectif de marge retiré"
            : "Objectif de marge enregistré",
      });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message:
          error instanceof DomainError
            ? error.message
            : "Une erreur inattendue est survenue.",
      }),
  });
}

/**
 * Régler les taux de majoration.
 *
 * L'invalidation va jusqu'au pilotage : les taux ne réécrivent aucune semaine
 * passée, mais une fiche ouverte doit relire la configuration pour afficher
 * les nouveaux taux dans le formulaire.
 */
export function useSaveOvertimeRates() {
  const makeContext = useExecutionContext();
  const queryClient = useQueryClient();
  const tenant = useTenantOptional();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (rates: SaveOvertimeRatesInput) =>
      tenantConfigServices.enregistrerMajorations(makeContext(), rates, serviceDeps),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenant-config", tenant?.id] });
      addToast({
        type: "success",
        title: "Taux de majoration enregistrés",
        message: "Ils s'appliquent aux semaines pointées ou corrigées à partir de maintenant.",
      });
    },

    onError: (error) =>
      addToast({
        type: "error",
        title: "Enregistrement impossible",
        message:
          error instanceof DomainError ? error.message : "Une erreur inattendue est survenue.",
      }),
  });
}
